import errorLogger from './frontendErrorLogger';

/**
 * Override the console.error function to also log errors to the backend
 * This allows us to capture all console.error calls and send them to our error tracking system
 */
export const setupConsoleErrorCapture = () => {
  // Store the original console.error method
  const originalConsoleError = console.error;
  
  // Flag to prevent recursive calls
  let isLogging = false;

  // Helper function to get auth token from localStorage
  const getAuthToken = () => {
    try {
      const persistedState = localStorage.getItem('reduxState');
      if (persistedState) {
        const state = JSON.parse(persistedState);
        const token = state?.auth?.token;
        return token;
      }
      return null;
    } catch (e) {
      // Don't use console.error here to avoid infinite loop
      return null;
    }
  };

  // Create a direct logger that bypasses the console.error override
  const directBackendLog = async (message, metadata = {}) => {
    try {
      const token = getAuthToken();
      const endpoint = token ? '/errorLogs/frontend' : '/errorLogs/frontend/anonymous';
      
      // Construct the API URL the same way errorLogger does
      let apiUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:4000' 
        : `${window.location.origin}`;
        
      // If we're on production domain www.bidd.is, make sure we're using the correct API URL
      if (window.location.hostname === 'www.bidd.is' || window.location.hostname === 'bidd.is') {
        apiUrl = process.env.REACT_APP_BACKEND_URL || 'https://bidd-live-backend.onrender.com';
      }
      
      // Capture detailed route information
      const routeInfo = {
        fullUrl: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
      };
      
      const payload = {
        message: typeof message === 'string' ? message : JSON.stringify(message),
        stack: new Error('Console Error').stack,
        location: 'ConsoleErrorCapture',
        metadata: {
          type: 'frontend-error',
          url: routeInfo.fullUrl,
          route: routeInfo.pathname,
          routeInfo: routeInfo,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          consoleError: true,
          source: 'console',
          ...metadata
        }
      };

      const headers = {
        'Content-Type': 'application/json'
      };

      // Add authorization token if provided
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Send directly to backend without using console
      await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include'
      });
    } catch (err) {
      // Silent fail to avoid loops - just let the original console.error do its job
    }
  };

  // Override console.error to log to backend as well
  console.error = function() {
    // Always call the original console.error with all arguments
    originalConsoleError.apply(console, arguments);
    
    // Don't try to log if we're already in the process of logging
    // This prevents infinite recursion
    if (isLogging) return;
    
    try {
      isLogging = true;
      
      // Convert arguments to array
      const args = Array.from(arguments);
      // Get the first argument which is typically the error message
      const message = args[0];
      
      // Skip logging our own error logger errors to avoid loops
      if (
        (typeof message === 'string' && 
         (message.includes('Error logging to backend failed') || 
          message.includes('Error in console.error override'))) ||
        (message && message.message && 
         (message.message.includes('Error logging to backend failed') ||
          message.message.includes('Error in console.error override')))
      ) {
        isLogging = false;
        return;
      }
      
      // Log directly to backend without using errorLogger to avoid loops
      directBackendLog(message, {
        consoleArgs: args.slice(1).map(arg => 
          typeof arg === 'string' ? arg : JSON.stringify(arg)
        )
      });
    } catch (e) {
      // Silent fail
    } finally {
      isLogging = false;
    }
  };

  // Return a cleanup function
  return () => {
    // Restore original console.error on cleanup
    console.error = originalConsoleError;
  };
};

export default setupConsoleErrorCapture;
